import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Modal,
  SeloStatusContrato,
  type StatusContrato,
} from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { useCarteira } from '../../hooks/useOperacao';
import { listarCarteira, type ParteDaOperacao } from '../../servicos/operacao';
import type { ModoDaAba } from './AbaIcmsRecorrente';
import { useIdentidade } from '../../hooks/useSessao';
import { ErroDaApi } from '../ErroDaApi';

/** RN-042 — uma minuta entre as quais o assessor escolhe. `GERAL`: da aba Contratos (D-020). */
interface ModeloDeContrato {
  readonly id: string;
  readonly dono: 'FORNECEDOR' | 'ASSESSOR' | 'GERAL';
  /** O nome que a empresa deu ao modelo — ela pode ter vários (item 16 de 15/09). */
  readonly nome: string;
  readonly nomeDaEmpresa: string;
  readonly atualizadoEm: string;
}

interface EstadoDoContrato {
  readonly de: StatusContrato;
  readonly proximos: readonly StatusContrato[];
  readonly fornecedorId: string | null;
  readonly parceiroId: string | null;
  /**
   * Mês de início (0-based) do contrato atual. Depois de uma reabertura
   * parcial ele é o mês do corte, e as transições seguintes precisam mandá-lo
   * de volta — senão o próximo passo gravaria a linha em janeiro.
   */
  readonly mesInicio: number | null;
  /**
   * RN-106 — os próximos passos que **quem olha** pode dar: o assessor vê só o
   * dele, o admin vê todos. `proximos` continua sendo a regra do estado.
   */
  readonly proximosPermitidos?: readonly StatusContrato[];
  /** As minutas de fornecedor e assessor da operação, para o passo do assessor. */
  readonly modelosDisponiveis: readonly ModeloDeContrato[];
  /** O admin pode declarar o contrato assinado fora do sistema e fechar direto. */
  readonly permiteAssinaturaManual: boolean;
  /** Meses (0-based) cujo e-mail do cronograma já saiu — não se reabre por cima deles. */
  readonly mesesComFluxoIniciado: readonly number[];
  /** RN-621 — o assessor que a tela deixa escolhido; a operadora, sem nenhum. */
  readonly assessorSugerido: string | null;
  /** De onde a reabertura parte: `null` = ano inteiro; `12` = nada a reabrir. */
  readonly reaberturaAPartirDe: number | null;
  readonly contratos: readonly {
    readonly status: StatusContrato;
    readonly mes: number;
    readonly fornecedorId: string;
  }[];
}

interface ResultadoTransicao {
  readonly de: string;
  readonly para: string;
  readonly efeitosAplicados: readonly string[];
  readonly efeitosComFalha: readonly { efeito: string; motivo: string }[];
}

/**
 * RN-018 a RN-030 — conduzir o contrato pela máquina de estados.
 *
 * ## O que estava faltando
 *
 * A API tinha a transição inteira desde o começo, com a tabela de estados, a
 * matriz de quem conduz cada etapa e os efeitos (PDF, D4Sign, cronograma,
 * e-mails). Não havia botão: dava para ver o status do contrato e não para
 * mudá-lo. Fechar contrato — a ação que transforma cálculo em operação — só
 * acontecia por chamada direta à API.
 *
 * ## Um passo por vez, e só os permitidos
 *
 * A tabela do domínio (`ABERTO → APROVACAO_ASSESSOR → APROVACAO_FORNECEDOR →
 * EM_ASSINATURA → FECHADO`) é a mesma aqui: a tela oferece apenas os destinos
 * válidos a partir do estado atual. O legado aceitava qualquer status no corpo
 * e aplicava os efeitos do destino sem olhar a origem — era assim que um
 * contrato ia parar em `FECHADO` sem PDF, sem assinatura e sem cronograma.
 *
 * Reabrir aparece separado, e não na mesma lista: é a operação de correção, sai
 * de qualquer estado, e misturá-la com "avançar" convida ao clique errado.
 *
 * ## Quem decide o estado é o servidor
 *
 * Um cliente pode ter dois contratos no mesmo ano, com fornecedores diferentes
 * e **estados diferentes** — 34 pares (cliente, ano) em produção. Qual deles é
 * "o atual" é regra de domínio, não "o mais recente". A primeira versão desta
 * tela usava o status que a listagem traz e, nesses 34 casos, oferecia um botão
 * que o servidor recusava com 409. Agora o estado e as transições vêm de
 * `GET /icms/contrato/:clienteId/:ano`.
 *
 * ## Efeitos que falham não escondem
 *
 * A transição vale mesmo quando um efeito não-bloqueante falha (RN-024) — um
 * e-mail que não saiu não desfaz um contrato fechado. Mas a tela **mostra** o
 * que falhou: no legado isso era engolido, e o contribuinte ficava sem o aviso
 * sem ninguém saber.
 */
export function TransicaoDoContrato(props: {
  readonly clienteId: string;
  readonly ano: number;
  /** Ausente = a operação. Em `leitura`, só a situação, sem botão. */
  readonly modo?: ModoDaAba;
}): JSX.Element {
  const modo = props.modo ?? 'operacao';
  const [aberto, setAberto] = useState(false);
  /**
   * Conta as aberturas do modal. É a chave que o re-semeia.
   *
   * A chave anterior era composta pelos dados do contrato (fornecedor,
   * assessor, mês de início). Parecia certo — re-semear quando o contrato muda
   * por baixo — mas a transição muda exatamente esses dados: o sucesso
   * invalida a consulta, o estado volta diferente, a chave muda e o modal
   * REMONTA no meio do sucesso, voltando ao formulário como se nada tivesse
   * acontecido. Quem clicava "Fechar contrato" via o botão girar e a tela
   * ficar igual; só ao fechar o modal o novo status aparecia.
   *
   * Contar aberturas dá o mesmo efeito desejado (cada abertura parte do
   * estado atual) sem o efeito indesejado (nada remonta com o modal aberto).
   */
  const [abertura, setAbertura] = useState(0);
  const abrir = (): void => {
    setAbertura((n) => n + 1);
    setAberto(true);
  };
  const [destino, setDestino] = useState<StatusContrato | null>(null);
  const [assinadoManualmente, setAssinadoManualmente] = useState(false);

  const estado = useQuery({
    queryKey: ['estado-contrato', props.clienteId, props.ano],
    queryFn: () =>
      requisicaoAutenticada<EstadoDoContrato>(`/icms/contrato/${props.clienteId}/${props.ano}`),
  });

  if (estado.data === undefined) {
    return (
      <p className="mt-4 text-sm text-conteudo-sutil">
        {estado.isError ? 'Não foi possível ler a situação do contrato.' : 'Carregando situação…'}
      </p>
    );
  }

  const atual = estado.data.de;
  // Só os passos de quem olha: o servidor diz quais são (RN-106) e confere de
  // novo na transição. Quem só acompanha não ganha botão nenhum.
  const permitidos =
    modo === 'leitura' ? [] : (estado.data.proximosPermitidos ?? estado.data.proximos);
  const avancos = permitidos.filter((s) => s !== 'ABERTO');
  const podeReabrir = permitidos.includes('ABERTO');
  const split = estado.data.contratos.length > 1;

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-card border border-borda bg-superficie-base px-4 py-3">
        <span className="text-sm text-conteudo-secundario">Situação do contrato:</span>
        <SeloStatusContrato status={atual} />
        {split && (
          /*
            Com vários contratos no ano, dizer só o status esconde de onde ele
            veio. Um cliente com 13 contratos, 12 fechados e 1 aberto, é
            "Aberto" — porque no aberto ainda há trabalho (RN-030) —, e sem esta
            linha isso parece erro de tela.
          */
          <span className="text-xs text-conteudo-sutil">
            {resumoDoSplit(estado.data.contratos)} — a ação vale sobre o vigente
          </span>
        )}

        <span className="ml-auto flex flex-wrap gap-2">
          {avancos.map((s) => (
            <Botao
              key={s}
              tamanho="pequeno"
              onClick={() => {
                setDestino(s);
                setAssinadoManualmente(false);
                abrir();
              }}
            >
              {ROTULO_DA_ACAO[s]}
            </Botao>
          ))}
          {estado.data.permiteAssinaturaManual && (
            /*
              O contrato foi feito e assinado fora daqui — em papel, ou num
              D4Sign que não é o desta instalação. O admin fecha direto, sem
              PDF nem envio; o cronograma e a recepção saem do mesmo jeito.
              Secundário de propósito: o caminho normal é o de cima.
            */
            <Botao
              variante="secundario"
              tamanho="pequeno"
              onClick={() => {
                setDestino('FECHADO');
                setAssinadoManualmente(true);
                abrir();
              }}
            >
              O contrato foi gerado e assinado manualmente
            </Botao>
          )}
          {podeReabrir && (
            <Botao
              variante="sutil"
              tamanho="pequeno"
              onClick={() => {
                setDestino('ABERTO');
                setAssinadoManualmente(false);
                abrir();
              }}
            >
              Reabrir
            </Botao>
          )}
        </span>
      </div>

      {/*
        A chave re-semeia os campos quando o contrato muda por baixo.

        O modal fica montado o tempo todo (é ele que anima a abertura), e
        `useState(props.fornecedorId)` só corre na montagem. Sem a chave, depois
        de uma reabertura o seletor continuava exibindo o fornecedor do contrato
        desfeito — a mesma herança indevida que os documentos do Deca 1 apontam,
        só que na tela em vez do banco.
      */}
      <ModalDeTransicao
        key={abertura}
        clienteId={props.clienteId}
        ano={props.ano}
        de={atual}
        para={destino}
        assinadoManualmente={assinadoManualmente}
        fornecedorId={estado.data.fornecedorId}
        parceiroId={estado.data.parceiroId}
        assessorSugerido={estado.data.assessorSugerido}
        mesInicioAtual={estado.data.mesInicio}
        mesesComFluxoIniciado={estado.data.mesesComFluxoIniciado}
        reaberturaAPartirDe={estado.data.reaberturaAPartirDe}
        operacao={modo === 'operacao'}
        aberto={aberto}
        aoFechar={() => {
          setAberto(false);
          setDestino(null);
          setAssinadoManualmente(false);
        }}
      />
    </>
  );
}

/* ─── Modal ───────────────────────────────────────────────────────────────── */

function ModalDeTransicao(props: {
  readonly clienteId: string;
  readonly ano: number;
  readonly de: StatusContrato;
  readonly para: StatusContrato | null;
  /** Fechamento declarado: sem PDF, sem D4Sign. Só chega `true` para admin. */
  readonly assinadoManualmente: boolean;
  readonly fornecedorId: string | null;
  readonly parceiroId: string | null;
  /** RN-621 — o assessor do cliente, já escolhido quando não há outro na linha. */
  readonly assessorSugerido: string | null;
  /** Mês de início (0-based) do contrato atual — ver `EstadoDoContrato.mesInicio`. */
  readonly mesInicioAtual: number | null;
  readonly mesesComFluxoIniciado: readonly number[];
  readonly reaberturaAPartirDe: number | null;
  /** O admin escolhe na carteira inteira; os demais, nos fornecedores com quem operam. */
  readonly operacao: boolean;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const identidade = useIdentidade();

  /** Reabrir a partir de um mês é troca de fornecedor no meio do ano (RN-023). */
  const reabertura = props.para === 'ABERTO';

  /**
   * O contrato atual começou depois de janeiro? As transições seguintes
   * continuam nele.
   *
   * Depois de uma reabertura parcial (jan e fev enviados, corte em março) a
   * linha ABERTO nasce em março. Esta tela mandava os passos seguintes sem
   * `mesInicio`, e o "Fechar contrato" gravava a linha nova em janeiro — por
   * cima do trecho que ficou fechado com o fornecedor antigo. O servidor
   * também herda o mês por conta própria; aqui o valor vai explícito para o
   * assessor **ver** de que mês o contrato parte, e poder mudar se souber
   * mais. Na reabertura fica em branco: o corte lá é calculado.
   */
  const mesInicioHerdado =
    !reabertura && props.mesInicioAtual !== null && props.mesInicioAtual > 0
      ? String(props.mesInicioAtual)
      : '';

  const [fornecedorId, setFornecedorId] = useState(props.fornecedorId ?? '');
  /*
    RN-621 — o assessor do cliente já vem escolhido: é ele quem trouxe o
    cliente. Sem assessor no cadastro, a operadora. Trocar continua a um
    clique, e "Sem assessor" continua na lista.
  */
  const [parceiroId, setParceiroId] = useState(props.parceiroId ?? props.assessorSugerido ?? '');
  const [mesInicio, setMesInicio] = useState(mesInicioHerdado);
  const [desagioVenda, setDesagioVenda] = useState('');
  const [contratoTemplateId, setContratoTemplateId] = useState('');

  /*
    A carteira de fornecedores é rota da operação (`/icms/suppliers`). Para o
    assessor, a lista é a dos fornecedores com quem a empresa dele opera
    (`/icms/settings/fornecedores`, RN-117) — sem isto o seletor do passo dele
    abria vazio.
  */
  const fornecedores = useQuery({
    queryKey: ['fornecedores-da-escolha', props.operacao],
    queryFn: () =>
      props.operacao
        ? listarCarteira('fornecedores')
        : requisicaoAutenticada<ParteDaOperacao[]>('/icms/settings/fornecedores'),
    enabled: props.aberto,
    staleTime: 5 * 60_000,
  });
  const assessores = useCarteira('assessores');

  /**
   * Quem escolhe o fornecedor é o **assessor**, não o cliente.
   *
   * Esta tela pedia fornecedor, deságio e mês de início já no primeiro passo —
   * e isso contraria o fluxo M19 (RN-019), que é a razão de existir do
   * fornecedor placeholder: o cliente fecha **sem escolher**, porque nesse
   * momento ele ainda não sabe de quem vai comprar. Quem sabe é o assessor, que
   * aprova em seguida e aí define fornecedor, deságio de venda e a partir de que
   * mês aquele fornecedor atende.
   *
   * A máquina de estados sempre soube disso — `ATORES_POR_DESTINO` dá
   * `APROVACAO_ASSESSOR` ao cliente e `APROVACAO_FORNECEDOR` ao parceiro. Era a
   * tela que pedia a informação da pessoa errada, uma etapa cedo.
   */
  const etapaDoAssessor = props.para === 'APROVACAO_FORNECEDOR';

  /**
   * RN-042 — as minutas entre as quais o assessor escolhe.
   *
   * Dependem de **quem** ele está escolhendo como fornecedor e assessor, e
   * isso muda dentro do modal — por isso a lista é consultada de novo a cada
   * troca, com a prévia das partes, e não lida do estado que a tela de fora
   * carregou (lá o contrato ainda aponta para o placeholder da operadora).
   */
  const modelos = useQuery({
    queryKey: ['estado-contrato', props.clienteId, props.ano, 'modelos', fornecedorId, parceiroId],
    queryFn: () => {
      // "Sem assessor" viaja como `parceiroId=` vazio, de propósito: a prévia
      // é a dupla (fornecedor, assessor) inteira, e o servidor precisa saber
      // que o assessor foi **tirado** — omitir o campo deixava a lista cair
      // no assessor gravado na linha, e a minuta dele aparecia aqui enquanto
      // o pedido, lendo só o corpo, a recusava.
      const previa = new URLSearchParams();
      previa.set('fornecedorId', fornecedorId);
      previa.set('parceiroId', parceiroId);
      return requisicaoAutenticada<EstadoDoContrato>(
        `/icms/contrato/${props.clienteId}/${props.ano}?${previa.toString()}`,
      );
    },
    enabled: props.aberto && etapaDoAssessor && fornecedorId !== '',
    select: (dados) => dados.modelosDisponiveis,
  });
  const modelosDisponiveis = modelos.data ?? [];
  // A escolha anterior não sobrevive à troca de fornecedor/assessor: a id
  // pode ser de uma empresa que saiu da operação. Sem escolha válida, vale o
  // primeiro da lista — o servidor já a manda na precedência (fornecedor,
  // assessor, geral), e a tela vem com ele marcado (D-020).
  const modeloEscolhido = modelosDisponiveis.some((m) => m.id === contratoTemplateId)
    ? contratoTemplateId
    : (modelosDisponiveis[0]?.id ?? '');

  const transicionar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<ResultadoTransicao>('/icms/contrato', {
        metodo: 'POST',
        corpo: {
          clienteId: props.clienteId,
          ano: props.ano,
          status: props.para,
          ...(fornecedorId === '' ? {} : { fornecedorId }),
          // No passo do assessor, "Sem assessor" vai como `null` explícito —
          // a mesma dupla que a prévia das minutas consultou. Nos demais
          // passos o campo só vai quando há valor, como antes.
          ...(etapaDoAssessor
            ? { parceiroId: parceiroId === '' ? null : parceiroId }
            : parceiroId === ''
              ? {}
              : { parceiroId }),
          ...(mesInicio === '' ? {} : { mesInicio: Number(mesInicio) }),
          ...(desagioVenda === ''
            ? {}
            : { desagioVendaPercentual: Number(desagioVenda.replace(',', '.')) }),
          ...(props.assinadoManualmente ? { assinadoManualmente: true } : {}),
          // RN-042 — o modelo marcado na tela (o escolhido, ou o primeiro da
          // lista) viaja; o servidor confere que ele é um dos oferecidos.
          ...(etapaDoAssessor && modeloEscolhido !== ''
            ? { contratoTemplateId: modeloEscolhido }
            : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['estado-contrato', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['cliente', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['cronograma', props.clienteId] });
      /*
        A apuração também muda: escolher o fornecedor materializa o deságio de
        venda mês a mês, e é a tabela de resultado que mostra isso. Sem estas
        duas linhas, a coluna do fornecedor só aparecia depois de recarregar a
        página à mão.
      */
      void queryClient.invalidateQueries({ queryKey: ['speds', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-cliente', props.clienteId] });
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
    },
  });

  const fechar = (): void => {
    transicionar.reset();
    setMesInicio(mesInicioHerdado);
    setDesagioVenda('');
    setContratoTemplateId('');
    props.aoFechar();
  };

  const irrevogavel = props.para === 'FECHADO';

  /**
   * Reabrir sem mês: o servidor calcula o corte pelos meses com fluxo.
   *
   * Dezembro já em fluxo (`12`) é "não há o que reabrir" — o botão fica
   * desabilitado em vez de deixar o servidor recusar depois do clique.
   */
  const nadaAReabrir =
    reabertura &&
    mesInicio === '' &&
    props.reaberturaAPartirDe !== null &&
    props.reaberturaAPartirDe > 11;
  const explicacao = props.assinadoManualmente
    ? EXPLICACAO_ASSINATURA_MANUAL
    : EXPLICACAO[props.para ?? 'ABERTO'];
  const rotulo =
    props.para === null
      ? ''
      : props.assinadoManualmente
        ? 'Fechar como assinado manualmente'
        : ROTULO_DA_ACAO[props.para];

  return (
    <Modal
      aberto={props.aberto && props.para !== null}
      aoFechar={fechar}
      titulo={rotulo}
      descricao={`Exercício ${props.ano}`}
      largura="larga"
      rodape={
        transicionar.isSuccess ? (
          <Botao onClick={fechar}>Concluir</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              variante={props.para === 'ABERTO' ? 'perigo' : 'primario'}
              carregando={transicionar.isPending}
              disabled={
                (etapaDoAssessor && fornecedorId === '') ||
                (etapaDoAssessor && modelos.isPending) ||
                nadaAReabrir
              }
              onClick={() => transicionar.mutate()}
            >
              {rotulo}
            </Botao>
          </>
        )
      }
    >
      {transicionar.isSuccess ? (
        <div className="flex flex-col gap-3">
          <Aviso tom="positivo" titulo={`Contrato em ${humanizar(transicionar.data.para)}`}>
            {transicionar.data.efeitosAplicados.length} efeito(s) aplicado(s):{' '}
            {transicionar.data.efeitosAplicados.map(humanizar).join(', ') || 'nenhum'}.
          </Aviso>

          {transicionar.data.efeitosComFalha.length > 0 && (
            <Aviso tom="atencao" titulo="A transição valeu, mas nem tudo saiu">
              <ul className="mt-1 flex list-disc flex-col gap-1 pl-4">
                {transicionar.data.efeitosComFalha.map((f) => (
                  <li key={f.efeito}>
                    <strong>{humanizar(f.efeito)}:</strong> {f.motivo}
                  </li>
                ))}
              </ul>
              <p className="mt-2">
                Efeito não-bloqueante que falha não desfaz a transição — mas precisa ser refeito à
                mão. O e-mail, pelo botão de reenvio; o PDF, refazendo o passo.
              </p>
            </Aviso>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Aviso tom={irrevogavel ? 'atencao' : 'informacao'} titulo={explicacao.titulo}>
            {explicacao.texto}
          </Aviso>

          {/*
            O passo do assessor: é aqui que a operação ganha fornecedor, preço e
            recorte de meses. Antes disso o contrato existe com o placeholder da
            operadora, e é justamente o que o assessor vem substituir.
          */}
          {etapaDoAssessor && (
            <>
              <CampoDeSelecao
                rotulo="Fornecedor do crédito"
                ajuda="Quem vende o crédito. Obrigatório: é ele quem aprova em seguida e assina o contrato."
                value={fornecedorId}
                onChange={(e) => setFornecedorId(e.target.value)}
                opcoes={[
                  { valor: '', rotulo: 'Selecione…' },
                  ...(fornecedores.data ?? []).map((f) => ({
                    valor: f.businessId,
                    rotulo: f.razaoSocial,
                  })),
                ]}
              />
              <CampoDeSelecao
                rotulo="Assessor"
                ajuda="Quem intermedia. Sem assessor, a operação é direta entre cliente e fornecedor."
                value={parceiroId}
                onChange={(e) => setParceiroId(e.target.value)}
                opcoes={[
                  { valor: '', rotulo: 'Sem assessor' },
                  ...(assessores.data ?? []).map((a) => ({
                    valor: a.businessId,
                    rotulo: a.razaoSocial,
                  })),
                ]}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <CampoDeSelecao
                  rotulo="A partir do mês"
                  ajuda={
                    mesInicioHerdado === ''
                      ? 'Split de fornecedor (RN-021): deixe em branco para o ano inteiro. Escolhendo um mês, este fornecedor atende dali em diante e os meses anteriores ficam com o anterior.'
                      : `O contrato atual começa em ${MESES[Number(mesInicioHerdado)] ?? ''}: os meses anteriores ficaram fechados com o fornecedor que os atendeu. Mude só se souber de algo que o sistema não sabe.`
                  }
                  value={mesInicio}
                  onChange={(e) => setMesInicio(e.target.value)}
                  opcoes={[
                    { valor: '', rotulo: 'Ano inteiro' },
                    ...MESES.map((m, i) => ({ valor: String(i), rotulo: m })),
                  ]}
                />
                <Campo
                  rotulo="Deságio de venda (%)"
                  inputMode="decimal"
                  ajuda="O que o cliente paga. A diferença para o deságio do fornecedor é o spread do assessor."
                  value={desagioVenda}
                  onChange={(e) => setDesagioVenda(e.target.value)}
                />
              </div>

              {/*
                RN-042 / D-020 — qual modelo vira o PDF: os do fornecedor e do
                assessor e os gerais da aba Contratos. Com vários, a lista já
                vem marcada (fornecedor, assessor, geral) e o assessor troca
                se quiser. Com um, a tela só diz qual será; sem nenhum, o
                esqueleto padrão. A pergunta só aparece depois de escolher o
                fornecedor — antes disso não há de quem listar.
              */}
              {fornecedorId !== '' && (
                <EscolhaDoModelo
                  carregando={modelos.isPending}
                  modelos={modelosDisponiveis}
                  escolhido={modeloEscolhido}
                  aoEscolher={setContratoTemplateId}
                />
              )}
            </>
          )}

          {/*
            Fora do passo do assessor não há campo de mês — mas o valor
            herdado continua indo no pedido. A frase é para quem aprova saber
            que está aprovando o trecho de março em diante, não o ano inteiro.
          */}
          {!etapaDoAssessor && !reabertura && mesInicioHerdado !== '' && (
            <p className="text-sm text-conteudo-secundario">
              Este contrato vale a partir de {MESES[Number(mesInicioHerdado)] ?? ''}: os meses
              anteriores ficaram fechados com o fornecedor que os atendeu.
            </p>
          )}

          {/*
            Reabrir não pergunta mais o mês: o servidor calcula o corte pelo
            último mês com fluxo iniciado — os meses cujo e-mail já saiu ficam
            fechados com o fornecedor que os atendeu, e o ano reabre dali em
            diante. Sem nenhum fluxo, reabre o ano inteiro. O admin pode
            sobrescrever, mas não por cima de um mês já em fluxo.
          */}
          {reabertura && (
            <>
              <p className="text-sm text-conteudo-secundario">
                {descreverReabertura(props.reaberturaAPartirDe)}
              </p>
              {identidade?.isAdmin === true && (
                <CampoDeSelecao
                  rotulo="Reabrir a partir de (sobrescrever)"
                  ajuda="Deixe no automático, salvo se souber de algo que o sistema não sabe. Meses com e-mail já enviado não podem ser escolhidos."
                  value={mesInicio}
                  onChange={(e) => setMesInicio(e.target.value)}
                  opcoes={[
                    { valor: '', rotulo: 'Automático' },
                    ...MESES.map((m, i) => ({
                      valor: String(i),
                      rotulo: props.mesesComFluxoIniciado.includes(i)
                        ? `${m} (já teve fluxo)`
                        : `A partir de ${m}`,
                      desabilitada: props.mesesComFluxoIniciado.includes(i),
                    })),
                  ]}
                />
              )}
            </>
          )}

          {transicionar.isError && (
            <ErroDaApi titulo="A transição não aconteceu" erro={transicionar.error} />
          )}
        </div>
      )}
    </Modal>
  );
}

/* ─── Modelo de contrato ──────────────────────────────────────────────────── */

function EscolhaDoModelo(props: {
  readonly carregando: boolean;
  readonly modelos: readonly ModeloDeContrato[];
  readonly escolhido: string;
  readonly aoEscolher: (id: string) => void;
}): JSX.Element {
  if (props.carregando) {
    return <p className="text-sm text-conteudo-sutil">Verificando as minutas disponíveis…</p>;
  }

  if (props.modelos.length === 0) {
    return (
      <p className="text-sm text-conteudo-secundario">
        <strong>Modelo de contrato:</strong> não há minuta das partes nem modelo geral para Créditos
        ICMS Recorrente. Será usado o modelo padrão.
      </p>
    );
  }

  if (props.modelos.length === 1) {
    const unico = props.modelos[0]!;
    return (
      <p className="text-sm text-conteudo-secundario">
        <strong>Modelo de contrato:</strong> {rotuloDoModelo(unico)}. É o único modelo disponível, e
        será usado no PDF.
      </p>
    );
  }

  return (
    <CampoDeSelecao
      rotulo="Modelo de contrato"
      ajuda="Já vem marcado o do fornecedor (senão o do assessor, senão o geral). Troque se quiser: o marcado vira o PDF que vai para assinatura."
      value={props.escolhido}
      onChange={(e) => props.aoEscolher(e.target.value)}
      opcoes={props.modelos.map((m) => ({ valor: m.id, rotulo: rotuloDoModelo(m) }))}
    />
  );
}

function rotuloDoModelo(m: ModeloDeContrato): string {
  if (m.dono === 'GERAL') return `Modelo geral: ${m.nome}`;
  return `${m.dono === 'FORNECEDOR' ? 'Fornecedor' : 'Assessor'} — ${m.nomeDaEmpresa}: ${m.nome}`;
}

/* ─── Tabelas ─────────────────────────────────────────────────────────────── */

/**
 * O rótulo diz **quem age em seguida**, não o nome do estado.
 *
 * "Fechar contrato" é o que o cliente faz; o que acontece depois é o assessor
 * receber o pedido. Botão nomeado pelo estado de destino obriga quem opera a
 * traduzir a máquina de estados de cabeça.
 */
const ROTULO_DA_ACAO: Readonly<Record<StatusContrato, string>> = {
  ABERTO: 'Reabrir contrato',
  APROVACAO_ASSESSOR: 'Fechar contrato',
  APROVACAO_FORNECEDOR: 'Aprovar e escolher o fornecedor',
  EM_ASSINATURA: 'Aprovar e gerar o contrato',
  FECHADO: 'Marcar como assinado',
};

/**
 * O fechamento declarado, quando o contrato não passou pelo D4Sign daqui.
 *
 * O texto é a confirmação: quem clica precisa ler que nenhum documento será
 * gerado nem enviado, porque não há como desfazer um "fechado" sem reabrir.
 */
const EXPLICACAO_ASSINATURA_MANUAL = {
  titulo: 'O D4Sign não será acionado',
  texto:
    'Você está declarando que o contrato deste exercício já foi gerado e assinado fora do sistema. Nenhum PDF será montado e nada será enviado ao D4Sign — o contrato vai direto para "Fechado". O cronograma de e-mails é criado e o contribuinte recebe o aviso de recepção, como em qualquer fechamento.',
} as const;

const EXPLICACAO: Readonly<Record<StatusContrato, { titulo: string; texto: string }>> = {
  ABERTO: {
    titulo: 'Reabrir preserva os meses que já tiveram fluxo',
    texto:
      'Os meses cujo e-mail do cronograma já saiu continuam fechados com o fornecedor que os atendeu, com o cronograma intacto. Do mês seguinte em diante, os e-mails agendados são removidos e o contrato volta a aberto — o próximo fechamento define o fornecedor dali em diante. Se nenhum mês teve fluxo, o ano inteiro é desfeito: fornecedor e benefício saem dos cálculos.',
  },
  APROVACAO_ASSESSOR: {
    titulo: 'O cliente fecha; o assessor escolhe o fornecedor',
    texto:
      'Aqui o fornecedor ainda não é definido — o contrato nasce com a operadora como marcador, e é o assessor quem coloca o fornecedor real no passo seguinte. Sai o e-mail de solicitação para os decisores do assessor, e os cálculos do ano ficam travados a partir daqui.',
  },
  APROVACAO_FORNECEDOR: {
    titulo: 'O assessor define fornecedor, preço e meses',
    texto:
      'O fornecedor escolhido substitui o marcador da operadora, o deságio de venda é materializado mês a mês e sai o e-mail para os decisores do fornecedor, que aprova em seguida.',
  },
  EM_ASSINATURA: {
    titulo: 'O fornecedor aprova; o PDF é gerado e vai assinar',
    texto:
      'O contrato é montado a partir da minuta deste fornecedor, vira PDF e segue para o D4Sign. Cliente e fornecedor precisam ter alguém marcado como "assina pela empresa" — sem isso o passo recusa antes de mudar o status.',
  },
  FECHADO: {
    titulo: 'Assinado: começa a operação do ano',
    texto:
      'O cronograma de e-mails é criado e o contribuinte recebe o aviso de recepção. Desfazer depois exige reabrir, e o cronograma já criado permanece.',
  },
};

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

function humanizar(chave: string): string {
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** O que a reabertura automática vai fazer, em uma frase. */
function descreverReabertura(aPartirDe: number | null): string {
  if (aPartirDe === null) {
    return 'Nenhum mês teve fluxo iniciado: o ano inteiro será reaberto.';
  }
  if (aPartirDe > 11) {
    return 'Todos os meses do ano já tiveram fluxo iniciado — não há o que reabrir neste exercício.';
  }
  return `Reabrir a partir de ${MESES[aPartirDe] ?? ''} — os meses anteriores já tiveram fluxo e continuam fechados.`;
}

/**
 * "13 contratos no ano: 12 fechados, 1 aberto".
 *
 * A contagem por status é o que explica o selo: sem ela, um cliente cujo
 * contrato vigente está aberto no meio de doze fechados parece defeito.
 */
function resumoDoSplit(contratos: readonly { readonly status: StatusContrato }[]): string {
  const porStatus = new Map<StatusContrato, number>();
  for (const c of contratos) porStatus.set(c.status, (porStatus.get(c.status) ?? 0) + 1);

  const partes = [...porStatus.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([status, n]) => `${n} ${ROTULO_CURTO[status]}`);

  return `${contratos.length} contratos no ano: ${partes.join(', ')}`;
}

const ROTULO_CURTO: Readonly<Record<StatusContrato, string>> = {
  ABERTO: 'aberto(s)',
  APROVACAO_ASSESSOR: 'com o assessor',
  APROVACAO_FORNECEDOR: 'com o fornecedor',
  EM_ASSINATURA: 'em assinatura',
  FECHADO: 'fechado(s)',
};
